/**
 * MarginBreakdownChart's pure scale + stack maths, split out so the chart file is
 * components only (CD-1 file budget). No React, no DOM — directly testable.
 */

import { PILLAR_SERIES_HEX } from '../pillarSeries'
import { COST_COMPONENTS, type BreakdownBar } from '../../../viewmodels/orderBook'

/** Cost-component fills, in stack order, from the validated 5-slot categorical palette.
 * Green and red stay out of it deliberately — they are the status vocabulary, and margin
 * (green) sits directly above this block. */
export const fillsFor = (theme: 'light' | 'dark'): string[] => [...PILLAR_SERIES_HEX[theme]]

export const kLabel = (v: number): string =>
  Math.abs(v) >= 1000 ? `£${Math.round(v / 1000)}k` : `£${Math.round(v)}`

export const truncate = (s: string): string => (s.length > 14 ? `${s.slice(0, 13)}…` : s)

const quantile = (sorted: number[], q: number): number =>
  sorted[Math.min(sorted.length - 1, Math.max(0, Math.round(q * (sorted.length - 1))))]

/** GM% axis domain — always spans 0 so a loss reads as below the baseline, and built from
 * the values inside a Tukey far-outlier fence (Q1−3·IQR, Q3+3·IQR). One tiny line can post
 * −5,776% GM on £540 of value; scaling to it flattens every other point into a single
 * band. Outliers are neither dropped nor moved: `clipOf` marks them and the chart draws
 * them as a triangle on the edge they left through, with the true figure in the tooltip. */
export function gmDomain(bars: BreakdownBar[]): [number, number] {
  const gms = bars.map((b) => b.gmPct).filter((v): v is number => v !== null).sort((a, b) => a - b)
  if (gms.length === 0) return [0, 1]
  const q1 = quantile(gms, 0.25)
  const q3 = quantile(gms, 0.75)
  const fence = 3 * (q3 - q1)
  const inside = gms.filter((v) => v >= q1 - fence && v <= q3 + fence)
  const kept = inside.length ? inside : gms
  const lo = Math.min(0, kept[0])
  const hi = Math.max(0, kept[kept.length - 1])
  const pad = (hi - lo) * 0.08 || 1
  return [lo - pad, hi + pad]
}

/** Where a point leaves the scale, if it does — 'above' / 'below' / null. */
export const clipOf = (pct: number, lo: number, hi: number): 'above' | 'below' | null =>
  pct > hi ? 'above' : pct < lo ? 'below' : null

export const clippedCount = (bars: BreakdownBar[], lo: number, hi: number): number =>
  bars.filter((b) => b.gmPct !== null && clipOf(b.gmPct, lo, hi) !== null).length

/** The single-block cost key, used when the row carries no usable component split. */
export const COST_KEY = 'cost'

/** The COST bar as Recharts stack fields — split into its components where the basis
 * carries them, one block where it does not, NOTHING AT ALL where nothing is priced (an
 * unpriced child draws no cost bar; the absence is the honest signal).
 *
 * Margin is deliberately not among these keys. It is not a quantity of the same kind as
 * material or machine time: stacking it on cost makes the bar total revenue, forces the
 * reader to subtract by eye to see cost, and squeezes the four small components into
 * slivers against one large block. Value gets its own bar alongside, and the gap between
 * the two IS the margin — which reads in one glance. */
export function costFields(bar: BreakdownBar, splitCost: boolean): Record<string, number> {
  if (bar.cost === null) return {}
  const out: Record<string, number> = {}
  let running = 0
  if (splitCost && bar.components) {
    COST_COMPONENTS.forEach((name) => {
      const size = bar.components?.[name] ?? 0
      if (size <= 0) return // a zero slot draws nothing rather than a hairline
      out[name] = size
      running += size
    })
  }
  return running === 0 ? { [COST_KEY]: bar.cost } : out
}

/** The £ axis has to hold both bars and the margin line, so it spans the largest of them —
 * a cost bar taller than its value bar (a loss) must not run off the top. */
export function valueDomain(bars: BreakdownBar[]): number {
  const tops = bars.flatMap((b) => [b.value, b.cost ?? 0, b.margin ?? 0])
  return Math.max(...tops, 1) * 1.08
}

/** One category on the plot, in the flat shape Recharts wants. Projection is Recharts'
 * job now — this only decides WHAT is plotted, which is domain policy. */
export interface BreakdownRow extends Record<string, unknown> {
  label: string
  value: number
  margin: number | null
  /** GM% clamped INTO the domain, so an outlier sits on the edge it left through rather
   * than at a height the data never reached. `clip` says it was clamped; `gmTrue` keeps
   * the real figure for the tooltip. Neither dropped nor moved silently. */
  gmPlot: number | null
  gmTrue: number | null
  clip: 'above' | 'below' | null
  loss: boolean
  bar: BreakdownBar
}

export function buildRows(bars: BreakdownBar[], splitCost: boolean, [lo, hi]: [number, number]): BreakdownRow[] {
  return bars.map((bar) => {
    const clip = bar.gmPct === null ? null : clipOf(bar.gmPct, lo, hi)
    return {
      label: bar.label,
      value: bar.value,
      margin: bar.margin,
      gmPlot: bar.gmPct === null ? null : clip ? (clip === 'above' ? hi : lo) : bar.gmPct,
      gmTrue: bar.gmPct,
      clip,
      loss: bar.margin !== null && bar.margin < 0,
      bar,
      ...costFields(bar, splitCost),
    }
  })
}
